import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/knowledge-base")({
  head: () => ({
    meta: [
      { title: "Knowledge Base, HOWDEN" },
      {
        name: "description",
        content:
          "Notes from the house: the 4Cs, how lab-grown diamonds are cultivated, and choosing a shape.",
      },
      { property: "og:title", content: "Knowledge Base, HOWDEN" },
      {
        property: "og:description",
        content:
          "Notes from the house: the 4Cs, how lab-grown diamonds are cultivated, and choosing a shape.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/knowledge-base" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/knowledge-base" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Notes from the house: the 4Cs, cultivation, and shape",
          description:
            "Editorial notes on lab-grown diamonds: how they are cultivated, what the 4Cs mean, and how shape changes everything.",
          author: { "@type": "Organization", name: "HOWDEN" },
          publisher: {
            "@type": "Organization",
            name: "HOWDEN",
            url: "https://howdendiamonds.com/",
          },
          mainEntityOfPage: "https://howdendiamonds.com/knowledge-base",
        }),
      },
    ],
  }),
  component: KnowledgeBasePage,
});

function KnowledgeBasePage() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-20 lg:px-12 lg:py-32">
      <header className="max-w-2xl">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          Knowledge Base
        </p>
        <h1 className="mt-6 font-serif text-5xl font-light leading-[1.05] lg:text-7xl">
          Notes from the <em className="italic text-accent-foreground/80">house</em>.
        </h1>
        <p className="mt-8 text-base leading-relaxed text-muted-foreground">
          A short library on the things that matter when choosing a stone, what
          the grades mean, how the diamond is made, and how shape changes
          everything.
        </p>
      </header>

      <nav className="mt-16 grid gap-px border border-hairline bg-hairline sm:grid-cols-3">
        {[
          { href: "#the-4cs", num: "I", label: "The 4Cs" },
          { href: "#how-grown", num: "II", label: "How They're Grown" },
          { href: "#choosing-shape", num: "III", label: "Choosing a Shape" },
        ].map((item) => (
          <a
            key={item.href}
            href={item.href}
            className="group flex flex-col gap-3 bg-background p-8 transition-colors hover:bg-secondary/40"
          >
            <span className="font-serif text-xs italic text-muted-foreground">
              {item.num}
            </span>
            <span className="font-serif text-2xl font-light leading-tight">
              {item.label}
            </span>
            <span className="mt-2 text-[10px] uppercase tracking-luxe text-muted-foreground transition-colors group-hover:text-foreground">
              Read →
            </span>
          </a>
        ))}
      </nav>

      <Article id="the-4cs" eyebrow="Essay I" title="The 4Cs, plainly.">
        <p>
          Every diamond is judged by four measures, established by the GIA in the
          1950s and now used worldwide. They are not equal. Cut comes first.
        </p>
        <Section title="Cut">
          <p>
            Cut is the only C the diamond doesn't arrive with, it's earned by
            the cutter. Proportion, symmetry, polish: these decide how light
            enters the stone, bounces between facets, and returns to your eye.
            A poorly cut D-flawless diamond will look dull. A well-cut G-VS2
            will outshine it.
          </p>
          <p>
            We list cut grades the GIA way: <em>Excellent, Very Good, Good, Fair,
            Poor</em>. HOWDEN holds to Excellent and, occasionally, Very Good.
          </p>
        </Section>
        <Section title="Color">
          <p>
            Graded D (colorless) through Z (light yellow). The difference between
            D and G is invisible to almost everyone outside the trade, and
            invisible once set in white gold or platinum. We curate D, E, and F.
          </p>
        </Section>
        <Section title="Clarity">
          <p>
            A measure of internal inclusions and surface blemishes, viewed at
            10× magnification. The scale runs FL (flawless) → IF → VVS1/VVS2 →
            VS1/VS2 → SI1/SI2 → I1/I2/I3. Anything VS2 or above is "eye-clean":
            no inclusions visible without a loupe.
          </p>
        </Section>
        <Section title="Carat">
          <p>
            Weight, not size. One carat is 0.2 grams. A round brilliant of 1.00ct
            measures roughly 6.5mm across the top; 2.00ct measures roughly 8.1mm
           , wider, but not double. Visual size grows with the cube root of
            weight, which is why a half-carat looks larger than its number
            suggests.
          </p>
        </Section>
      </Article>

      <Article
        id="how-grown"
        eyebrow="Essay II"
        title="How a lab diamond is grown."
      >
        <p>
          A lab-grown diamond is a diamond. Same carbon lattice, same hardness,
          same refractive index, same fire. The only difference is where the
          carbon came from and how long it took.
        </p>
        <Section title="The seed">
          <p>
            Every cultivated stone starts with a thin slice of an existing
            diamond, the seed. From there, two methods take over.
          </p>
        </Section>
        <Section title="HPHT, High Pressure, High Temperature">
          <p>
            The seed is placed in a press with pure carbon and a metal flux.
            Pressure climbs to roughly 1.5 million pounds per square inch;
            temperature to 1,500°C. These are the conditions found 150km below
            the earth's surface. Carbon dissolves into the flux, migrates onto
            the seed, and crystallises. A few weeks later: a rough diamond.
          </p>
        </Section>
        <Section title="CVD, Chemical Vapor Deposition">
          <p>
            The seed is sealed in a vacuum chamber filled with a carbon-rich gas,
            usually methane. Microwaves ionise the gas into plasma. Carbon atoms
            rain down onto the seed, layer by atomic layer, in conditions closer
            to those found in interstellar space than under the earth. Slower
            than HPHT, with finer control over the result.
          </p>
        </Section>
        <Section title="What arrives">
          <p>
            Either method produces rough diamond crystal that is then cut and
            polished by hand, exactly as a mined stone would be. The finished
            diamond is graded by IGI or GIA, scored on the 4Cs, and laser-inscribed
            with its certificate number on the girdle.
          </p>
          <p>
            There is no machine that can tell a lab diamond from a mined one
            without specialist instruments, because there is nothing chemical to
            tell apart.
          </p>
        </Section>
      </Article>

      <Article
        id="choosing-shape"
        eyebrow="Essay III"
        title="Choosing a shape."
      >
        <p>
          Shape is the most personal decision in the process. It changes how the
          stone reads on the hand, what kind of brilliance it returns, and which
          settings will sit well with it. A short field guide.
        </p>
        <ShapeNote name="Round Brilliant">
          The default for a reason. 57 facets engineered to maximise return of
          white light. Most forgiving of slight flaws, most expensive per carat,
          works in any setting.
        </ShapeNote>
        <ShapeNote name="Oval">
          The face-up size of a round brilliant in roughly 10% less weight.
          Elongates the finger. Watch for the "bow-tie", a dark band across the
          centre in a poorly cut oval.
        </ShapeNote>
        <ShapeNote name="Emerald">
          Step-cut, not brilliant, long parallel facets that act like a hall of
          mirrors. Less fire, more clarity. Demands a high clarity grade
          (VS1 or better) because every flaw is visible.
        </ShapeNote>
        <ShapeNote name="Cushion">
          A square or rectangle with rounded corners. Softer than a princess,
          warmer than a round. Pillowy in setting; suits vintage references and
          halos.
        </ShapeNote>
        <ShapeNote name="Pear">
          Brilliant-cut, asymmetric, round at one end, pointed at the other.
          Worn point-toward-the-nail. Like the oval, can show a bow-tie if cut
          shallow.
        </ShapeNote>
        <ShapeNote name="Princess">
          A square brilliant. Sharper, more modern than a cushion. Sets close to
          the finger; corners must be protected with V-tip prongs.
        </ShapeNote>
        <ShapeNote name="Marquise · Radiant · Asscher · Heart">
          Less common, more characterful. The marquise elongates the finger
          dramatically; the radiant balances brilliance with crisp lines; the
          asscher is the emerald's square cousin; the heart speaks for itself.
        </ShapeNote>
        <p className="mt-12">
          A practical rule: if you don't know, choose a round. If you know, choose
          what you've always quietly wanted.
        </p>
      </Article>

      <div className="mt-32 border-t border-hairline pt-12">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          Begin
        </p>
        <h2 className="mt-4 font-serif text-3xl font-light">
          Browse the jewelry.
        </h2>
        <a
          href="/shop"
          className="mt-8 inline-block border-b border-foreground pb-1 text-xs uppercase tracking-luxe"
        >
          View hand-made pieces
        </a>
      </div>
    </div>
  );
}

function Article({
  id,
  eyebrow,
  title,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article id={id} className="mt-32 scroll-mt-28 border-t border-hairline pt-16">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        {eyebrow}
      </p>
      <h2 className="mt-4 font-serif text-4xl font-light leading-tight lg:text-5xl">
        {title}
      </h2>
      <div className="mt-10 max-w-2xl space-y-6 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </article>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-10">
      <h3 className="font-serif text-2xl font-light text-foreground">{title}</h3>
      <div className="mt-3 space-y-4">{children}</div>
    </div>
  );
}

function ShapeNote({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-8 grid gap-2 border-l border-hairline pl-6 sm:grid-cols-[160px_1fr] sm:gap-8 sm:border-l-0 sm:pl-0">
      <p className="font-serif text-lg text-foreground">{name}</p>
      <p>{children}</p>
    </div>
  );
}
